/**
 * Unsaved changes, app-wide (#3878, #3903; born as the «Correo › Proveedor» guard of #3793).
 *
 * A form registers what the operator has changed (and how to save it) with
 * `useRegisterUnsavedChanges`. While an entry is registered, `UnsavedChangesNavigationBlocker`
 * (the ONLY `useBlocker` of the app) asks `shouldBlock` about every in-app navigation and, when
 * it blocks, hands the decision to `requestLeave`: a three-way dialog (keep editing · leave
 * without saving · save and leave). «Save and leave» leaves only when the save came back
 * `'saved'`. Every path that closes the dialog without leaving calls `cancel()`: a blocker left
 * in `blocked` freezes all navigation.
 *
 * Several forms may be registered at once: the app blocks if ANY of them leaves, «save and leave»
 * saves each one that leaves, in order (those already saved stay saved if a later one fails), and
 * «leave without saving» discards all of them. If saving throws or takes longer than
 * `SAVE_DEADLINE_MS` the dialog STAYS open with the reason and nothing navigates (a timed-out
 * save is only «not confirmed»: the request may still finish; a retry joins it).
 *
 * Auth exits are never blocked, not even for a frame: `/login`, any navigation while the session
 * is not active, and the redirects of a sign-out in progress (`sessionEnding.ts`). Full page loads
 * (reload, closing the tab) are covered by `beforeunload`, armed only while an entry is registered.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { logger } from '@/shared/utils/logger'

import { clearSessionEnding, isSessionEnding } from './sessionEnding'
import { UnsavedChangesDialog } from './UnsavedChangesDialog'

/** «Save and leave» that has not answered by then counts as a failure. */
const SAVE_DEADLINE_MS = 15_000

export type SaveFailure = 'failed' | 'timeout'

/** Runs a form callback; one that throws is logged, never allowed to take the app down. */
function runGuarded(what: string, to: string | undefined, callback: (() => void) | undefined) {
  try {
    callback?.()
  } catch (error) {
    logger.error(`Unsaved changes: ${what} threw`, { to, error })
  }
}

class SaveDeadlineError extends Error {
  constructor() {
    super('Saving before leaving timed out')
  }
}

type LateResult<T> = { ok: true; value: T } | { ok: false; error: unknown }

/**
 * Resolves with the save, or rejects with `SaveDeadlineError` once the deadline passes. A save
 * that settles AFTER the deadline never navigates (the dialog already reported it as not
 * confirmed), but its real result is handed to `onLate` so it is never lost silently.
 */
function withDeadline<T>(promise: Promise<T>, onLate: (result: LateResult<T>) => void): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  let timedOut = false
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      timedOut = true
      reject(new SaveDeadlineError())
    }, SAVE_DEADLINE_MS)
  })
  // Also keeps a failure after the deadline from being an unhandled rejection.
  promise.then(
    (value) => {
      if (timedOut) onLate({ ok: true, value })
    },
    (error: unknown) => {
      if (timedOut) onLate({ ok: false, error })
    },
  )
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer))
}

export type LeaveOutcome = 'saved' | 'invalid' | 'needs-confirmation' | 'conflict' | 'failed'

interface LeaveLocation {
  pathname: string
  search: string
}

const pathnameChanged = (current: LeaveLocation, next: LeaveLocation) =>
  current.pathname !== next.pathname

export interface UnsavedChangesEntry {
  /** Changed keys, in the order of the form. */
  fields: readonly string[]
  /** The same fields as a sentence («el remitente y «Responder a»»); never a value. */
  fieldsLabel: string
  /**
   * Saves the form. If a save is already in flight it JOINS it (no second request), so the
   * success notice and the focus follow-up come from this single path.
   */
  saveForLeave: () => Promise<LeaveOutcome>
  /**
   * True while a save is in flight: the exit then starts «save and leave» by itself.
   * Must read a ref, not state: it is asked at the moment of the click.
   */
  isSaving?: () => boolean
  /**
   * True while ANOTHER save of the form is running that «save and leave» must not join or race
   * (the editor's «save as new version»). The dialog then disables «Save and leave» and says why;
   * `saveForLeave` is never called. A plain value (state), not a getter: the dialog re-renders.
   */
  saveBlocked?: boolean
  /**
   * Called after «save and leave» did NOT leave and the dialog is already closed (its focus trap
   * has given the focus back), so the form can focus a field or open its own confirmation.
   */
  afterDialogClosed?: () => void
  /**
   * «Save and leave» failed after the operator had already left the form (it unmounted while the
   * save ran): its banner is gone, so the form says it some other way (a notice).
   */
  onFailedAfterLeaving?: () => void
  /** Called when the operator picks «Leave without saving», right before the navigation. */
  onDiscard?: () => void
  /**
   * True when going from `current` to `next` unmounts the form (so it must ask). Default: the
   * pathname changes. A form that lives in a query parameter (a section) declares it here.
   */
  leavesOn?: (current: LeaveLocation, next: LeaveLocation) => boolean
}

interface LeaveRequest {
  /** Where the operator is and where the navigation goes: decides which forms leave. */
  current: LeaveLocation
  next: LeaveLocation
  /** Lets the navigation through. */
  proceed: () => void
  /** The navigation is abandoned (keep editing, Escape, ✕, a save that did not leave). */
  cancel: () => void
  /** Pathname of the destination, only to name it in the log. */
  to?: string | undefined
}

interface Registry {
  register: (entry: UnsavedChangesEntry) => void
  /** Tells whether `entry` currently forbids «save and leave» (see `UnsavedChangesEntry.saveBlocked`). */
  setSaveBlocked: (entry: UnsavedChangesEntry, blocked: boolean) => void
  unregister: (entry: UnsavedChangesEntry) => void
  /** Whether this navigation must ask. `false` without an entry, before reading anything else. */
  shouldBlock: (current: LeaveLocation, next: LeaveLocation) => boolean
  /** Opens the dialog (or starts «save and leave» if a save is in flight). */
  requestLeave: (request: LeaveRequest) => void
}

interface PendingLeave extends LeaveRequest {
  /** The registered forms that leave with this navigation, in registration order. */
  entries: UnsavedChangesEntry[]
  fieldsLabel: string
  fieldCount: number
}

const UnsavedChangesContext = createContext<Registry | null>(null)

export function UnsavedChangesProvider({
  children,
  sessionActive = true,
}: {
  children: ReactNode
  /** False once the session is gone: nothing is blocked and no form is asked about. */
  sessionActive?: boolean
}) {
  const { i18n } = useTranslation('common')
  const entriesRef = useRef<UnsavedChangesEntry[]>([])
  const sessionActiveRef = useRef(sessionActive)
  const [saveFailure, setSaveFailure] = useState<SaveFailure | null>(null)
  // The form whose save failed (as a sentence): the dialog names it.
  const [failedFields, setFailedFields] = useState<string | null>(null)
  // The failed form is gone from the screen: «Save and leave» cannot run its save again.
  const [failedFormGone, setFailedFormGone] = useState(false)
  const mountedRef = useRef(false)
  const [hasEntry, setHasEntry] = useState(false)
  const [pending, setPendingState] = useState<PendingLeave | null>(null)
  // Mirrors `pending` synchronously: a second request in the same tick (StrictMode's double
  // effect) must see the first one.
  const pendingRef = useRef<PendingLeave | null>(null)
  const [saving, setSaving] = useState(false)
  // Counts «save and leave» runs: a retry JOINS the earlier save, so only the newest run may say
  // that a late save finished (the earlier one would claim it was never confirmed).
  const runRef = useRef(0)
  const afterCloseRef = useRef<(() => void) | null>(null)

  const setPending = useCallback((next: PendingLeave | null) => {
    pendingRef.current = next
    setPendingState(next)
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const register = useCallback((entry: UnsavedChangesEntry) => {
    entriesRef.current = [...entriesRef.current, entry]
    setHasEntry(true)
  }, [])

  // Entries whose form forbids «save and leave» right now. A ref mirrors it for `saveAndLeave`.
  const [blockedEntries, setBlockedEntries] = useState<ReadonlySet<UnsavedChangesEntry>>(
    () => new Set(),
  )
  const blockedRef = useRef<ReadonlySet<UnsavedChangesEntry>>(blockedEntries)
  const setSaveBlocked = useCallback((entry: UnsavedChangesEntry, blocked: boolean) => {
    if (blockedRef.current.has(entry) === blocked) return
    const next = new Set(blockedRef.current)
    if (blocked) next.add(entry)
    else next.delete(entry)
    blockedRef.current = next
    setBlockedEntries(next)
  }, [])

  // Each registration clears only itself: a cleanup that arrives after a newer registration of
  // the same form (StrictMode's double mount, a changed field list) must not erase the newer one.
  const unregister = useCallback((entry: UnsavedChangesEntry) => {
    entriesRef.current = entriesRef.current.filter((candidate) => candidate !== entry)
    setHasEntry(entriesRef.current.length > 0)
    if (blockedRef.current.has(entry)) {
      const next = new Set(blockedRef.current)
      next.delete(entry)
      blockedRef.current = next
      setBlockedEntries(next)
    }
  }, [])

  // The session ended (sign-out, expiry): nobody is asked about anything any more, and whatever
  // was typed in a form is dropped WITHOUT a question. That is deliberate: signing out is the
  // operator's explicit intent (and an expired session cannot save anything, so a dialog offering
  // «Save and leave» would be a lie). The guard comes back on the next sign-in.
  useEffect(() => {
    sessionActiveRef.current = sessionActive
    if (sessionActive) {
      clearSessionEnding()
      return
    }
    entriesRef.current = []
    setHasEntry(false)
  }, [sessionActive])

  useEffect(() => {
    if (!hasEntry) return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Older browsers (Chrome < 119) ignore preventDefault and only ask when this is truthy.
      event.returnValue = true
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasEntry])

  // Runs once the dialog is gone: React flushes every passive cleanup (the focus trap giving the
  // focus back, synchronously in its cleanup) before the passive effects of the same commit, so
  // this focus is not overwritten.
  useEffect(() => {
    if (pending !== null || !afterCloseRef.current) return
    const run = afterCloseRef.current
    afterCloseRef.current = null
    // Runs above the route error boundary: a throw here would take the whole app down.
    runGuarded('afterDialogClosed', undefined, run)
  }, [pending])

  // The form that opened the dialog went away (a tab changed by other means): the dialog says
  // «if you leave, they are lost» about a form that no longer exists. Close it and release the
  // blocker; while a save is running it stays, and `saveAndLeave` closes it when that ends.
  useEffect(() => {
    // A failure on screen stays: it is the only place that says which form was not saved.
    if (!hasEntry && pending !== null && !saving && saveFailure === null) {
      pending.cancel()
      setPending(null)
      setSaveFailure(null)
      setFailedFields(null)
      setFailedFormGone(false)
    }
  }, [hasEntry, pending, saving, saveFailure, setPending])

  const joinFields = useCallback(
    (entries: readonly UnsavedChangesEntry[]) =>
      new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
        entries.map((entry) => entry.fieldsLabel),
      ),
    [i18n.language],
  )

  const leave = useCallback(
    (target: PendingLeave) => {
      setPending(null)
      setSaveFailure(null)
      setFailedFields(null)
      setFailedFormGone(false)
      // The provider itself is gone (the whole app unmounted while saving): nothing to navigate.
      if (mountedRef.current) target.proceed()
    },
    [setPending],
  )

  const stay = useCallback(
    (target: PendingLeave) => {
      setPending(null)
      setSaveFailure(null)
      setFailedFields(null)
      setFailedFormGone(false)
      target.cancel()
    },
    [setPending],
  )

  const saveAndLeave = useCallback(
    async (target: PendingLeave) => {
      // Another save of a form is running (the dialog disables the button; this covers any other
      // door): «save and leave» never starts a save on top of it.
      if (target.entries.some((entry) => blockedRef.current.has(entry))) {
        logger.warn('Save and leave refused: another save of the form is in progress', {
          to: target.to,
        })
        return
      }
      const runId = ++runRef.current
      setSaveFailure(null)
      setFailedFields(null)
      setFailedFormGone(false)
      setSaving(true)
      // Each form that leaves, in order. One that is no longer registered saved itself already
      // (its changes are gone) or went away: nothing left to save. The first failure stops the
      // run; the forms saved before it stay saved and are NOT offered (nor saved again) on retry.
      const saved = new Set<UnsavedChangesEntry>()
      // A navigation asked while saving (browser Back) replaces `pending`: the exit that ends this
      // run is the LAST one the operator asked for, not the one that started it.
      const latest = () => pendingRef.current ?? target
      // The dialog after a failure: only what is still unsaved, with the failed form named.
      const failWith = (entry: UnsavedChangesEntry, reason: SaveFailure) => {
        const entries = target.entries.filter(
          (candidate) =>
            candidate === entry ||
            (!saved.has(candidate) && entriesRef.current.includes(candidate)),
        )
        setPending({
          ...latest(),
          entries,
          fieldsLabel: joinFields(entries),
          fieldCount: entries.reduce((total, candidate) => total + candidate.fields.length, 0),
        })
        setFailedFields(entry.fieldsLabel)
        setFailedFormGone(!entriesRef.current.includes(entry))
        setSaveFailure(reason)
      }
      for (const entry of target.entries) {
        if (!entriesRef.current.includes(entry)) continue
        let outcome: LeaveOutcome
        try {
          outcome = await withDeadline(entry.saveForLeave(), (late) => {
            // The deadline had passed: nothing navigates, but the real result is recorded.
            const context = {
              form: entry.fieldsLabel,
              to: target.to,
              leftAlready: pendingRef.current === null,
            }
            if (!late.ok) {
              logger.error('Save failed after its leave deadline', {
                ...context,
                error: late.error,
              })
            } else if (runRef.current !== runId) {
              // A retry joined this save and reports its own result.
            } else if (late.value === 'saved') {
              logger.warn(
                'Save finished after its leave deadline: the operator was told it was not confirmed',
                context,
              )
            } else {
              logger.warn('Save answered after its leave deadline without saving', {
                ...context,
                outcome: late.value,
              })
            }
          })
        } catch (error) {
          // Thrown or too slow: the operator must NOT believe it saved. The dialog stays open
          // with the reason, the blocker stays blocked, nothing navigates.
          logger.error('Saving before leaving failed', {
            to: target.to,
            form: entry.fieldsLabel,
            savedBefore: [...saved].map((candidate) => candidate.fieldsLabel),
            error,
          })
          setSaving(false)
          // The form went away while saving: nobody would show the reason. The dialog names the
          // form when it is open (then no second notice); with no dialog the notice is the only voice.
          if (!entriesRef.current.includes(entry) && pendingRef.current === null) {
            runGuarded('onFailedAfterLeaving', target.to, entry.onFailedAfterLeaving)
            return
          }
          failWith(entry, error instanceof SaveDeadlineError ? 'timeout' : 'failed')
          return
        }
        if (outcome === 'saved') {
          saved.add(entry)
          continue
        }
        setSaving(false)
        // Still registered: the form shows why. Gone: nobody would, so it raises a notice.
        if (entriesRef.current.includes(entry))
          afterCloseRef.current = entry.afterDialogClosed ?? null
        else runGuarded('onFailedAfterLeaving', target.to, entry.onFailedAfterLeaving)
        return stay(latest())
      }
      setSaving(false)
      leave(latest())
    },
    [joinFields, leave, setPending, stay],
  )

  const shouldBlock = useCallback((current: LeaveLocation, next: LeaveLocation) => {
    if (entriesRef.current.length === 0) return false
    if (!sessionActiveRef.current || isSessionEnding() || next.pathname === '/login') return false
    return entriesRef.current.some((entry) => (entry.leavesOn ?? pathnameChanged)(current, next))
  }, [])

  const requestLeave = useCallback(
    (request: LeaveRequest) => {
      const entries = entriesRef.current.filter((entry) =>
        (entry.leavesOn ?? pathnameChanged)(request.current, request.next),
      )
      // Re-checked: the session may have ended between the block and this effect.
      if (entries.length === 0 || !sessionActiveRef.current || isSessionEnding()) {
        return request.proceed()
      }
      const target: PendingLeave = {
        ...request,
        entries,
        fieldsLabel: joinFields(entries),
        fieldCount: entries.reduce((total, entry) => total + entry.fields.length, 0),
      }
      const alreadyAsking = pendingRef.current !== null
      setPending(target)
      if (!alreadyAsking && entries.some((entry) => entry.isSaving?.())) void saveAndLeave(target)
    },
    [joinFields, saveAndLeave, setPending],
  )

  const discard = useCallback(
    (target: PendingLeave) => {
      for (const entry of target.entries) {
        try {
          entry.onDiscard?.()
        } catch (error) {
          // One form failing to reset must not stop the others, nor the exit the operator chose.
          logger.error('Discarding unsaved changes failed', { to: target.to, error })
        }
      }
      leave(target)
    },
    [leave],
  )

  const value = useMemo<Registry>(
    () => ({ register, unregister, setSaveBlocked, shouldBlock, requestLeave }),
    [register, unregister, setSaveBlocked, shouldBlock, requestLeave],
  )

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog
        isOpen={pending !== null}
        fieldsLabel={pending?.fieldsLabel ?? ''}
        fieldCount={pending?.fieldCount ?? 0}
        saving={saving}
        saveBlocked={pending?.entries.some((entry) => blockedEntries.has(entry)) ?? false}
        saveFailure={saveFailure}
        failedFields={failedFields}
        saveUnavailable={failedFormGone}
        onStay={() => pending && stay(pending)}
        onDiscard={() => pending && discard(pending)}
        onSaveAndLeave={() => pending && void saveAndLeave(pending)}
      />
    </UnsavedChangesContext.Provider>
  )
}

/** The form: registers `entry` while there are changes and `null` when there are none. */
export function useRegisterUnsavedChanges(entry: UnsavedChangesEntry | null): void {
  const registry = useContext(UnsavedChangesContext)
  // Read at the moment of use, so a `saveForLeave` recreated on every render does not
  // re-run the registration effect.
  const latest = useRef(entry)
  useEffect(() => {
    latest.current = entry
  })
  const active = entry !== null && entry.fields.length > 0
  const fieldsKey = entry?.fields.join(',') ?? ''
  const fieldsLabel = entry?.fieldsLabel ?? ''
  const saveBlocked = entry?.saveBlocked ?? false
  const mineRef = useRef<UnsavedChangesEntry | null>(null)

  useEffect(() => {
    if (!registry || !active) return
    const mine: UnsavedChangesEntry = {
      fields: fieldsKey.split(',').filter(Boolean),
      fieldsLabel,
      isSaving: () => Boolean(latest.current?.isSaving?.()),
      saveForLeave: () =>
        latest.current
          ? latest.current.saveForLeave()
          : (logger.warn('saveForLeave called after its form unregistered'),
            Promise.resolve('failed')),
      afterDialogClosed: () => latest.current?.afterDialogClosed?.(),
      onFailedAfterLeaving: () => latest.current?.onFailedAfterLeaving?.(),
      onDiscard: () => latest.current?.onDiscard?.(),
      leavesOn: (current, next) => (latest.current?.leavesOn ?? pathnameChanged)(current, next),
    }
    registry.register(mine)
    mineRef.current = mine
    return () => {
      mineRef.current = null
      registry.unregister(mine)
    }
  }, [registry, active, fieldsKey, fieldsLabel])

  // Declared after the registration effect: on a (re-)registration it runs again and tells the
  // provider the current value for the new entry object.
  useEffect(() => {
    if (registry && mineRef.current) registry.setSaveBlocked(mineRef.current, saveBlocked)
  }, [registry, active, fieldsKey, fieldsLabel, saveBlocked])
}

/** For `UnsavedChangesNavigationBlocker` only. `null` when no provider is mounted. */
export function useUnsavedChangesRegistry(): Registry | null {
  return useContext(UnsavedChangesContext)
}
